import { useEffect, useRef, useState } from "react";
import {
	type CompanySuggestion,
	fetchCompanySearch,
	MIN_COMPANY_SEARCH_LENGTH,
} from "~/lib/company-search";

type AutocompleteStatus = "idle" | "loading" | "success" | "error";

interface AutocompleteState {
	suggestions: CompanySuggestion[];
	status: AutocompleteStatus;
	error: string | null;
}

const IDLE_STATE: AutocompleteState = {
	suggestions: [],
	status: "idle",
	error: null,
};

export function useCompanyAutocomplete(query: string, debounceMs = 250): AutocompleteState {
	const [state, setState] = useState<AutocompleteState>(IDLE_STATE);
	const requestId = useRef(0);
	const canSearch = query.trim().length >= MIN_COMPANY_SEARCH_LENGTH;

	useEffect(() => {
		const term = query.trim();
		requestId.current += 1;
		const currentRequest = requestId.current;

		if (!canSearch) {
			setState(IDLE_STATE);
			return;
		}

		const controller = new AbortController();
		setState({ suggestions: [], status: "loading", error: null });

		const timer = window.setTimeout(async () => {
			try {
				const response = await fetchCompanySearch(term, 1, 8, controller.signal);
				if (requestId.current !== currentRequest) return;
				const suggestions = response.results.map(({ ico, name, city }) => ({ ico, name, city }));
				setState({ suggestions, status: "success", error: null });
			} catch (error) {
				if (controller.signal.aborted || requestId.current !== currentRequest) return;
				setState({
					suggestions: [],
					status: "error",
					error: error instanceof Error ? error.message : "Návrhy se nepodařilo načíst.",
				});
			}
		}, debounceMs);

		return () => {
			window.clearTimeout(timer);
			controller.abort();
		};
	}, [canSearch, debounceMs, query]);

	return canSearch ? state : IDLE_STATE;
}
